export interface MentionableUser {
  id: number;
  username: string | null;
  displayName: string | null;
  email?: string | null;
}

const normalizeString = (value: string | null | undefined) => {
  if (!value) return '';
  return value.trim();
};

const sanitizeLabel = (value: string) => value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

export const deriveMentionLabel = (user: MentionableUser) => {
  const display = normalizeString(user.displayName);
  if (display) {
    const initials = display
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((segment) => segment[0]?.toUpperCase() ?? '')
      .join('');
    if (initials) {
      return initials;
    }
  }

  const username = normalizeString(user.username);
  const email = normalizeString(user.email);
  const source = username || email;
  if (source) {
    const localPart = source.split('@')[0] || source;
    const sanitized = sanitizeLabel(localPart);
    if (sanitized.length >= 2) {
      return sanitized.slice(0, 2);
    }
    if (sanitized.length === 1) {
      return `${sanitized}${sanitized}`;
    }
  }

  return `U${user.id}`;
};

export const matchUserByLabel = <T extends MentionableUser>(users: T[], label: string) => {
  const target = sanitizeLabel(label);
  if (!target) return null;

  return (
    users.find((user) => {
      const derived = deriveMentionLabel(user);
      if (derived === target) return true;

      const display = sanitizeLabel(normalizeString(user.displayName));
      if (display && (display.startsWith(target) || display.includes(target))) return true;

      const username = sanitizeLabel(normalizeString(user.username));
      if (username && (username.startsWith(target) || username.includes(target))) return true;

      const emailLocal = sanitizeLabel(normalizeString(user.email)?.split('@')[0] || '');
      if (emailLocal && (emailLocal.startsWith(target) || emailLocal.includes(target))) return true;

      return false;
    }) || null
  );
};

export const buildMentionMarkup = (user: MentionableUser) => `@[${deriveMentionLabel(user)}](${user.id})`;

export const parseMentions = (text: string): { userId: number; userName: string }[] => {
  if (!text) return [];

  const mentionRegex = /@\[([^\]]+)\]\((\d+)\)/g;
  const mentions = [];
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    mentions.push({
      userId: Number.parseInt(match[2], 10),
      userName: match[1],
    });
  }

  return mentions;
};
